import { createReducer, on } from "@ngrx/store";
import { AutomationHardwareItem, AutomationVendorHardwareData, ClientDevice, EngineeringUpdateMeeting, EngineeringUpdateMeetingUserContent, FactoryNode, FactoryNodeConfig, KnowledgeDocument, LabelPrinter, LabelPrinterGroup, MergeArrayValues, RobotProgram, RobotProgramGroup, SoftwareTask, Tag, UserTimeSession } from "@prism/prism-shared";
import { AutomationActions } from ".";

export interface AutomationState {

    factoryNodes: FactoryNode[];
    factoryNodeConfigs: FactoryNodeConfig[];

    robotProgramGroups: RobotProgramGroup[];
    robotPrograms: RobotProgram[];
    clientDevices: ClientDevice[];
    labelPrinters: LabelPrinter[];
    labelPrinterGroups: LabelPrinterGroup[];

    userTimeSessions: UserTimeSession[];
    softwareTasks: SoftwareTask[];

    engineeringUpdateMeetings: EngineeringUpdateMeeting[]
    engineeringUpdateMeetingUserContents: EngineeringUpdateMeetingUserContent[]
    knowledgeDocuments: KnowledgeDocument[];

    automationHardwareItems: AutomationHardwareItem[];
    automationVendorHardwareData: AutomationVendorHardwareData[];

    tags: Tag[]

    loadingState: number;
    error: any;
}

export const initialState: AutomationState = {
    factoryNodes: [],
    factoryNodeConfigs: [],

    robotProgramGroups: [],
    robotPrograms: [],
    clientDevices: [],
    labelPrinters: [],
    labelPrinterGroups: [],

    userTimeSessions: [],
    softwareTasks: [],

    engineeringUpdateMeetings: [],
    engineeringUpdateMeetingUserContents: [],
    knowledgeDocuments: [],

    automationHardwareItems: [],
    automationVendorHardwareData: [],

    tags: [],

    loadingState: 0,
    error: null,
};

export const automationReducer = createReducer(
    initialState,
    // =========================================================
    on(AutomationActions.LoadTags.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadTags.success, (state, { tags }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        tags,
        error: null
    })),
    on(AutomationActions.LoadTags.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.SetFactoryNode.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.SetFactoryNode.success, (state, { factoryNode }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        factoryNodes: MergeArrayValues(state.factoryNodes, factoryNode),
        error: null
    })),
    on(AutomationActions.SetFactoryNode.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadFactoryNodeById.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadFactoryNodeById.success, (state, { factoryNode }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        factoryNodes: MergeArrayValues(state.factoryNodes, factoryNode),
        error: null
    })),
    on(AutomationActions.LoadFactoryNodeById.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadFactoryNodes.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadFactoryNodes.success, (state, { factoryNodes }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        factoryNodes,
        error: null
    })),
    on(AutomationActions.LoadFactoryNodes.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.SetFactoryNodeConfig.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.SetFactoryNodeConfig.success, (state, { factoryNodeConfig }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        factoryNodeConfigs: MergeArrayValues(state.factoryNodeConfigs, factoryNodeConfig),
        error: null
    })),
    on(AutomationActions.SetFactoryNodeConfig.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadFactoryNodeConfigById.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadFactoryNodeConfigById.success, (state, { factoryNodeConfig }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        factoryNodeConfigs: MergeArrayValues(state.factoryNodeConfigs, factoryNodeConfig),
        error: null
    })),
    on(AutomationActions.LoadFactoryNodeConfigById.failure, (state, { error }): AutomationState => {
        return {
            ...state,
            loadingState: state.loadingState - 1,
            error,
        }
    }),
    // =========================================================
    on(AutomationActions.LoadFactoryNodeConfigsByIds.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadFactoryNodeConfigsByIds.success, (state, { factoryNodeConfigs }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        factoryNodeConfigs: MergeArrayValues(state.factoryNodeConfigs, factoryNodeConfigs),
        error: null
    })),
    on(AutomationActions.LoadFactoryNodeConfigsByIds.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadFactoryNodeConfigsByFactoryNodeId.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadFactoryNodeConfigsByFactoryNodeId.success, (state, { factoryNodeConfigs }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        factoryNodeConfigs: MergeArrayValues(state.factoryNodeConfigs, factoryNodeConfigs),
        error: null
    })),
    on(AutomationActions.LoadFactoryNodeConfigsByFactoryNodeId.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.SetRobotProgramGroup.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.SetRobotProgramGroup.success, (state, { programGroup }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        robotProgramGroups: MergeArrayValues(state.robotProgramGroups, programGroup),
        error: null
    })),
    on(AutomationActions.SetRobotProgramGroup.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadRobotProgramGroups.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadRobotProgramGroups.success, (state, { programGroups }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        robotProgramGroups: programGroups,
        error: null
    })),
    on(AutomationActions.LoadRobotProgramGroups.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadRobotProgramGroupById.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadRobotProgramGroupById.success, (state, { programGroup }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        robotProgramGroups: MergeArrayValues(state.robotProgramGroups, programGroup),
        error: null
    })),
    on(AutomationActions.LoadRobotProgramGroupById.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadRobotProgramsByRobotProgramGroupId.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadRobotProgramsByRobotProgramGroupId.success, (state, { programs }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        robotPrograms: programs,
        error: null
    })),
    on(AutomationActions.LoadRobotProgramsByRobotProgramGroupId.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadRobotProgramById.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadRobotProgramById.success, (state, { program }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        robotPrograms: MergeArrayValues(state.robotPrograms, program),
        error: null
    })),
    on(AutomationActions.LoadRobotProgramById.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.DeleteRobotProgram.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.DeleteRobotProgram.success, (state, { programId }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        robotPrograms: state.robotPrograms.filter(p => p.id !== programId),
        error: null
    })),
    on(AutomationActions.DeleteRobotProgram.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.SetClientDevice.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.SetClientDevice.success, (state, { clientDevice }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        clientDevices: MergeArrayValues(state.clientDevices, clientDevice),
        error: null
    })),
    on(AutomationActions.SetClientDevice.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.SetLabelPrinter.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.SetLabelPrinter.success, (state, { labelPrinter }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        labelPrinters: MergeArrayValues(state.labelPrinters, labelPrinter),
        error: null
    })),
    on(AutomationActions.SetLabelPrinter.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadClientDevices.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadClientDevices.success, (state, { clientDevices }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        clientDevices,
        error: null
    })),
    on(AutomationActions.LoadClientDevices.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadLabelPrinters.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadLabelPrinters.success, (state, { labelPrinters }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        labelPrinters,
        error: null
    })),
    on(AutomationActions.LoadLabelPrinters.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadLabelPrinterGroups.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadLabelPrinterGroups.success, (state, { labelPrinterGroups }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        labelPrinterGroups,
        error: null
    })),
    on(AutomationActions.LoadLabelPrinterGroups.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.SetLabelPrinterGroup.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.SetLabelPrinterGroup.success, (state, { labelPrinterGroup }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        labelPrinterGroups: MergeArrayValues(state.labelPrinterGroups, labelPrinterGroup),
        error: null
    })),
    on(AutomationActions.SetLabelPrinterGroup.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.DeleteLabelPrinterGroup.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.DeleteLabelPrinterGroup.success, (state, { labelPrinterGroupId }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        labelPrinterGroups: state.labelPrinterGroups.filter(group => group.id !== labelPrinterGroupId),
        error: null
    })),
    on(AutomationActions.DeleteLabelPrinterGroup.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadClientDeviceById.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(AutomationActions.LoadClientDeviceById.success, (state, { clientDevice }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        clientDevices: MergeArrayValues(state.clientDevices, clientDevice),
        error: null
    })),
    on(AutomationActions.LoadClientDeviceById.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.SetUserTimeSession.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.SetUserTimeSession.success, (state, { userTimeSession }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        userTimeSessions: MergeArrayValues(state.userTimeSessions, userTimeSession),
        error: null,
    })),
    on(AutomationActions.SetUserTimeSession.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.SetUserTimeSessions.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.SetUserTimeSessions.success, (state, { userTimeSessions }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        userTimeSessions: MergeArrayValues(state.userTimeSessions, userTimeSessions),
        error: null,
    })),
    on(AutomationActions.SetUserTimeSessions.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadUserTimeSessions.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.LoadUserTimeSessions.success, (state, { userTimeSessions }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        userTimeSessions,
        error: null,
    })),
    on(AutomationActions.LoadUserTimeSessions.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(AutomationActions.DeleteUserTimeSessions.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.DeleteUserTimeSessions.success, (state, { userTimeSessionId }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        userTimeSessions: state.userTimeSessions.filter((t) => t.id !== userTimeSessionId),
        error: null,
    })),
    on(AutomationActions.DeleteUserTimeSessions.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(AutomationActions.LoadUserTimeSessionsByIds.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.LoadUserTimeSessionsByIds.success, (state, { userTimeSessions }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        userTimeSessions: MergeArrayValues(state.userTimeSessions, userTimeSessions),
        error: null,
    })),
    on(AutomationActions.LoadUserTimeSessionsByIds.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(AutomationActions.SetSoftwareTask.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.SetSoftwareTask.success, (state, { softwareTask }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        softwareTasks: MergeArrayValues(state.softwareTasks, softwareTask),
        error: null,
    })),
    on(AutomationActions.SetSoftwareTask.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadSoftwareTasks.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.LoadSoftwareTasks.success, (state, { softwareTasks }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        softwareTasks,
        error: null,
    })),
    on(AutomationActions.LoadSoftwareTasks.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.SetEngineeringUpdateMeeting.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.SetEngineeringUpdateMeeting.success, (state, { engineeringUpdateMeeting }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        engineeringUpdateMeetings: MergeArrayValues(state.engineeringUpdateMeetings, engineeringUpdateMeeting),
        error: null,
    })),
    on(AutomationActions.SetEngineeringUpdateMeeting.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(AutomationActions.LoadEngineeringUpdateMeetings.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.LoadEngineeringUpdateMeetings.success, (state, { engineeringUpdateMeetings }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        engineeringUpdateMeetings,
        error: null,
    })),
    on(AutomationActions.LoadEngineeringUpdateMeetings.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(AutomationActions.LoadEngineeringUpdateMeetingById.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.LoadEngineeringUpdateMeetingById.success, (state, { engineeringUpdateMeeting }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        engineeringUpdateMeetings: MergeArrayValues(state.engineeringUpdateMeetings, engineeringUpdateMeeting),
        error: null,
    })),
    on(AutomationActions.LoadEngineeringUpdateMeetingById.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(AutomationActions.SetEngineeringUpdateMeetingUserContents.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.SetEngineeringUpdateMeetingUserContents.success, (state, { engineeringUpdateMeetingUserContents }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        engineeringUpdateMeetingUserContents: MergeArrayValues(state.engineeringUpdateMeetingUserContents, engineeringUpdateMeetingUserContents),
        error: null,
    })),
    on(AutomationActions.SetEngineeringUpdateMeetingUserContents.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(AutomationActions.SetEngineeringUpdateMeetingUserContent.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.SetEngineeringUpdateMeetingUserContent.success, (state, { engineeringUpdateMeetingUserContent }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        engineeringUpdateMeetingUserContents: MergeArrayValues(state.engineeringUpdateMeetingUserContents, engineeringUpdateMeetingUserContent),
        error: null,
    })),
    on(AutomationActions.SetEngineeringUpdateMeetingUserContent.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(AutomationActions.LoadEngineeringUpdateMeetingUserContents.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.LoadEngineeringUpdateMeetingUserContents.success, (state, { engineeringUpdateMeetingUserContents }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        engineeringUpdateMeetingUserContents,
        error: null,
    })),
    on(AutomationActions.LoadEngineeringUpdateMeetingUserContents.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(AutomationActions.LoadEngineeringUpdateMeetingUserContentsByMeetingId.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.LoadEngineeringUpdateMeetingUserContentsByMeetingId.success, (state, { engineeringUpdateMeetingUserContents }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        engineeringUpdateMeetingUserContents,
        error: null,
    })),
    on(AutomationActions.LoadEngineeringUpdateMeetingUserContentsByMeetingId.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error
    })),
    // =========================================================
    on(AutomationActions.DeleteEngineeringUpdateMeetingUserContent.initial, (state, { engineeringUpdateMeetingUserContent }): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.DeleteEngineeringUpdateMeetingUserContent.success, (state, { engineeringUpdateMeetingUserContent }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        engineeringUpdateMeetingUserContents: state.engineeringUpdateMeetingUserContents.filter((c) => c.id !== engineeringUpdateMeetingUserContent.id),
        error: null,
    })),
    on(AutomationActions.DeleteEngineeringUpdateMeetingUserContent.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadKnowledgeDocuments.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.LoadKnowledgeDocuments.success, (state, { knowledgeDocuments }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        knowledgeDocuments,
        error: null,
    })),
    on(AutomationActions.LoadKnowledgeDocuments.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadKnowledgeDocumentById.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.LoadKnowledgeDocumentById.success, (state, { knowledgeDocument }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        knowledgeDocuments: MergeArrayValues(state.knowledgeDocuments, knowledgeDocument),
    })),
    on(AutomationActions.LoadKnowledgeDocumentById.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.SetKnowledgeDocument.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.SetKnowledgeDocument.success, (state, { knowledgeDocument }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        knowledgeDocuments: MergeArrayValues(state.knowledgeDocuments, knowledgeDocument),
        error: null,
    })),
    on(AutomationActions.SetKnowledgeDocument.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.DeleteKnowledgeDocumentById.initial, (state, { documentId }): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.DeleteKnowledgeDocumentById.success, (state, { documentId }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        knowledgeDocuments: state.knowledgeDocuments.filter((c) => c.id !== documentId),
        error: null,
    })),
    on(AutomationActions.DeleteKnowledgeDocumentById.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadAutomationHardwareItems.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.LoadAutomationHardwareItems.success, (state, { automationHardwareItems }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        automationHardwareItems,
        error: null,
    })),
    on(AutomationActions.LoadAutomationHardwareItems.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadAutomationHardwareItemById.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.LoadAutomationHardwareItemById.success, (state, { automationHardwareItem }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        automationHardwareItems: MergeArrayValues(state.automationHardwareItems, automationHardwareItem),
        error: null,
    })),
    on(AutomationActions.LoadAutomationHardwareItemById.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.SetAutomationHardwareItem.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.SetAutomationHardwareItem.success, (state, { automationHardwareItem }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        automationHardwareItems: MergeArrayValues(state.automationHardwareItems, automationHardwareItem),
        error: null,
    })),
    on(AutomationActions.SetAutomationHardwareItem.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.DeleteAutomationHardwareItem.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.DeleteAutomationHardwareItem.success, (state, { automationHardwareItemId }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        automationHardwareItems: state.automationHardwareItems.filter(i => i.id !== automationHardwareItemId),
        error: null,
    })),
    on(AutomationActions.DeleteAutomationHardwareItem.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.LoadAutomationVendorHardwareDataByHardwareItemId.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.LoadAutomationVendorHardwareDataByHardwareItemId.success, (state, { automationVendorHardwareData }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        automationVendorHardwareData: MergeArrayValues(state.automationVendorHardwareData, automationVendorHardwareData),
        error: null,
    })),
    on(AutomationActions.LoadAutomationVendorHardwareDataByHardwareItemId.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.SetAutomationVendorHardwareData.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.SetAutomationVendorHardwareData.success, (state, { automationVendorHardwareData }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        automationVendorHardwareData: MergeArrayValues(state.automationVendorHardwareData, automationVendorHardwareData),
        error: null,
    })),
    on(AutomationActions.SetAutomationVendorHardwareData.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(AutomationActions.DeleteAutomationVendorHardwareData.initial, (state): AutomationState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(AutomationActions.DeleteAutomationVendorHardwareData.success, (state, { automationVendorHardwareDataId }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        automationVendorHardwareData: state.automationVendorHardwareData.filter(v => v.id !== automationVendorHardwareDataId),
        error: null,
    })),
    on(AutomationActions.DeleteAutomationVendorHardwareData.failure, (state, { error }): AutomationState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================


)
